import { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import "./IndiceCriterios.css";

interface IndiceLink {
  id?: string;
  label?: string;
  path?: string;
  tabKey?: string;
  isPending?: boolean;
  
  isDestaqueAlteracao?: boolean;
  
  isAdequacaoSolicitada?: boolean;
  
  isAdequacaoAguardando?: boolean;
  
  isAdequacaoRespondida?: boolean;
}

interface IndiceItem {
  title?: string;
  links?: IndiceLink[];
}

interface IndiceCriteriosProps {
  open?: boolean;
  onClose?: () => void;
  items?: IndiceItem[];
  onNavigate?: (link: { path?: string; tabKey?: string; id?: string }) => void;
}

const limparNumeracaoDoInicio = (label?: string) => {
  if (!label) return "";

  return label.replace(/^\s*\d+(\.\d+)*\s*[.-]?\s*/u, "").trim();
};

export default function IndiceCriterios({
  open = false,
  onClose,
  items = [],
  onNavigate,
}: IndiceCriteriosProps) {
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);
  const safeItems = useMemo(
    () =>
      (Array.isArray(items) ? items : []).filter(
        (section) => (section.links?.length ?? 0) > 0,
      ),
    [items],
  );

  /* Escurecimento começa abaixo da faixa de abas (header + subnavbar), não sobre ela. */
  useLayoutEffect(() => {
    if (!open) {
      document.documentElement.style.removeProperty("--ic-top-offset");
      return;
    }

    const applyOffset = () => {
      const el = document.querySelector<HTMLElement>("[data-merit-ic-below]");
      const y = el ? el.getBoundingClientRect().bottom : 70;
      const px = y > 0 ? y : 70;
      document.documentElement.style.setProperty("--ic-top-offset", `${px}px`);
    };

    applyOffset();
    window.addEventListener("resize", applyOffset);
    return () => {
      window.removeEventListener("resize", applyOffset);
      document.documentElement.style.removeProperty("--ic-top-offset");
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => closeBtnRef.current?.focus?.(), 0);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const prevBody = document.body.style.overflow;
    const contentEl = document.querySelector<HTMLElement>(".content-area");
    const prevContent = contentEl ? contentEl.style.overflow : "";
    document.body.style.overflow = "hidden";
    if (contentEl) contentEl.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevBody;
      if (contentEl) contentEl.style.overflow = prevContent;
    };
  }, [open]);

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target?.dataset?.overlay === "true") onClose?.();
  };

  const handleNavigate = (link?: IndiceLink) => {
    if (!link) return;
    onNavigate?.(link);
    onClose?.();
  };

  const overlay = (
    <div
      className={`icOverlay ${open ? "isOpen" : ""}`}
      data-overlay="true"
      data-testid="ic-overlay"
      onMouseDown={handleOverlayClick}
      aria-hidden={!open}
    >
      <aside
        className={`icDrawer ${open ? "isOpen" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Índice de critérios"
      >
        <header className="icHeader">
          <h3 className="icTitle">ÍNDICE DE CRITÉRIOS</h3>

          <button
            ref={closeBtnRef}
            type="button"
            className="icCloseBtn"
            onClick={() => onClose?.()}
            aria-label="Fechar"
            title="Fechar"
          >
            ×
          </button>
        </header>

        <div className="icContent">
          {safeItems.length === 0 ? (
            <div className="icEmpty">Nenhum critério disponível no índice.</div>
          ) : (
            safeItems.map((section, idx) => (
              <div className="icSection" key={`${section.title || "sec"}-${idx}`}>
                <div className="icSectionTitle">{section.title}</div>

                <ol className="icList">
                  {(section.links || []).map((link, i) => {
                    const labelLimpo = limparNumeracaoDoInicio(link.label);
                    const destaqueAlt =
                      !link.isPending && Boolean(link.isDestaqueAlteracao);
                    const adequacaoSol = Boolean(link.isAdequacaoSolicitada);
                    const adequacaoAguard = Boolean(link.isAdequacaoAguardando);
                    const adequacaoResp = Boolean(link.isAdequacaoRespondida);

                    return (
                      <li
                        className={`icListItem ${link.isPending ? "isPending" : ""} ${destaqueAlt ? "isDestaqueAlteracao" : ""} ${adequacaoSol ? "isAdequacaoSolicitada" : ""} ${adequacaoAguard ? "isAdequacaoAguardando" : ""} ${adequacaoResp ? "isAdequacaoRespondida" : ""}`}
                        key={`${link.id || "item"}-${i}`}
                      >
                        <button
                          type="button"
                          className={`icLinkBtn ${link.isPending ? "isPending" : ""} ${destaqueAlt ? "isDestaqueAlteracao" : ""} ${adequacaoSol ? "isAdequacaoSolicitada" : ""} ${adequacaoAguard ? "isAdequacaoAguardando" : ""} ${adequacaoResp ? "isAdequacaoRespondida" : ""}`}
                          onClick={() => handleNavigate(link)}
                          title={
                            (labelLimpo || link.label || "") +
                            (adequacaoAguard
                              ? " — Adequação solicitada: aguardando sindicato"
                              : adequacaoResp
                                ? " — Adequação respondida pelo sindicato"
                                : adequacaoSol
                                  ? " — Adequação solicitada ao sindicato"
                                  : "")
                          }
                        >
                          <span
                            className={`icItemLabel ${link.isPending ? "isPending" : ""} ${destaqueAlt ? "isDestaqueAlteracao" : ""}`}
                          >
                            {labelLimpo || link.label}
                          </span>
                          {adequacaoSol ? (
                            <span className="icAdequacaoBadge" aria-hidden>
                              {adequacaoAguard
                                ? "Aguardando sindicato"
                                : adequacaoResp
                                  ? "Adequação respondida"
                                  : "Adequação enviada"}
                            </span>
                          ) : null}
                        </button>
                      </li>
                    );
                  })}
                </ol>
              </div>
            ))
          )}
        </div>
      </aside>
    </div>
  );

  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(overlay, document.body);
}